import { notification } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
    CANVAS_OBJECT_TYPE_PREFIX,
    EXPIRED_NOTIFICATION_KEY,
    RETURN_FROM_SUMMARY_STACK_PAUSE,
} from '@common/constants';
import { PAGE } from '@common/enums/page.enum';
import { alertWhenExiting, notifyFromStatus, statusChanged } from '@common/helpers';
import useEquivalentMatchingDisabled from '@common/hooks/useEquivalentMatchingDisabled/useEquivalentMatchingDisabled';
import { useRoomvoEventListener } from '@common/hooks/useRoomvoEventListener/useRoomvoEventListener';
import { EstimateStatus } from '@common/models/save-estimate-dto';
import FullPageLoader from '@components/FullPageLoader';
import { getNotificationsFor } from '@core/components';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import useEstimate from '@core/hooks/useEstimate';
import useEstimateStatus from '@core/hooks/useEstimateStatus';
import { useExpiredEstimateActions } from '@core/hooks/useExpiredEstimateActions';
import useFetchedEstimate from '@core/hooks/useFetchedEstimate';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { resetSelectedCornerRadius } from '@redux/slices/cornerRadiiSlice';
import { resetSelectedCustomSide } from '@redux/slices/customSideSlice';
import { resetSelectedDiagonalCorner } from '@redux/slices/diagonalCornerSlice';
import { setPauseUndoStackUpdatesForSummary } from '@redux/slices/drawingShapeSlice';
import {
    selectIsSavingEstimate,
    selectPrevEstimateStatus,
    updateEeStatusList,
    updateIsSavingEstimate,
    updatePrevEstimateStatus,
    updateValidSmallTicketNumber,
} from '@redux/slices/estimateSlice';
import { selectFabricCanvas } from '@redux/slices/fabricSlice';

import OrderSummaryPage from './components/order-summary/order-summary-page';
import drawingTool from './components/scrollable-area/design-countertop/drawer/drawingTool';
import ScrollableArea from './components/scrollable-area/scrollable-area';

import './easy-estimator-page.scss';

// eslint-disable-next-line sonarjs/cognitive-complexity
const EasyEstimatorPage = (): JSX.Element => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(PAGE.ESTIMATE);
    useEquivalentMatchingDisabled(); // this gets the useEffect to clean up edges when matching is disabled

    const dispatch = useAppDispatch();

    const canvas = useAppSelector(selectFabricCanvas);

    const { estimateStatus, fulfilledTimeStamp: statusTimestamp } = useEstimateStatus();
    const { restoreFromFetchedEstimate } = useEstimate();
    const {
        fetchedEstimate,
        isFetchingEstimate,
        isError: isFetchError,
        fulfilledTimeStamp: fetchedTimestamp,
    } = useFetchedEstimate();

    const previousStatus = useAppSelector(selectPrevEstimateStatus);

    const { isFetchingRetailerSettings } = useRetailerSettings();
    const isSavingEstimate = useAppSelector(selectIsSavingEstimate);

    const { page: pageFromAnalytics, track, trackCloneEstimate } = useAnalyticsEvents();
    const { openToNewEstimate, createCloneEstimate } = useExpiredEstimateActions();

    useRoomvoEventListener();

    const onCloneEstimate = () => {
        try {
            notification.close(EXPIRED_NOTIFICATION_KEY);
        } catch {
            // do nothing
        }
        trackCloneEstimate({ status: fetchedEstimate?.status ?? '' });
        createCloneEstimate();
    };

    const onNewEstimate = () => {
        openToNewEstimate();
    };

    useEffect(() => {
        window.addEventListener('beforeunload', alertWhenExiting);

        return () => {
            window.removeEventListener('beforeunload', alertWhenExiting);
        };
    }, []);

    //when the fetched status changes, not on initial estimate load
    useEffect(() => {
        if (
            !fetchedEstimate ||
            !estimateStatus ||
            !fetchedTimestamp ||
            !statusTimestamp ||
            fetchedTimestamp >= statusTimestamp // if we fetched more recently than status, just skip this
        ) {
            return;
        }
        const currentStatus = new EstimateStatus(estimateStatus);
        if (statusChanged(previousStatus, currentStatus)) {
            const statusList = notifyFromStatus({ estimateStatus: currentStatus, fetchedEstimate: fetchedEstimate });
            dispatch(updateEeStatusList(statusList));
            dispatch(updatePrevEstimateStatus(currentStatus));
        }
    }, [dispatch, estimateStatus, fetchedEstimate, fetchedTimestamp, previousStatus, statusTimestamp]);

    const haveWeFetchedAlready = useRef(false);

    //if we fetched a new estimate from the API, load it
    useEffect(() => {
        if (isFetchingEstimate || isFetchError || !fetchedEstimate || haveWeFetchedAlready.current) {
            // still fetching, don't bother
            return;
        }

        haveWeFetchedAlready.current = true;

        document.title = `${fetchedEstimate.customerName}: ${fetchedEstimate.estimateNumber}`;

        if (isSavingEstimate) {
            return;
        }

        //actually restore the estimate we just fetched
        restoreFromFetchedEstimate(fetchedEstimate);
        //and look for any cached messages (from a previous save)
        getNotificationsFor(fetchedEstimate.estimateNumber);
        //we haven't fetched a full status, but we have parts of it from the fetched estimate
        //use that as our status and present any status related notifications

        const currentStatus = new EstimateStatus({
            status: fetchedEstimate.status,
            paymentUrl: fetchedEstimate.paymentUrl,
        });
        const statusList = notifyFromStatus({
            estimateStatus: currentStatus,
            fetchedEstimate: fetchedEstimate,
            expiredCloneAction: onCloneEstimate,
            expiredNewAction: onNewEstimate,
        });
        dispatch(updateEeStatusList(statusList));

        //now save it for comparison with a future /estimate/status API update
        dispatch(updatePrevEstimateStatus(currentStatus));
    }, [
        isFetchingEstimate,
        isFetchError,
        fetchedEstimate,
        dispatch,
        t,
        restoreFromFetchedEstimate,
        isSavingEstimate,
        createCloneEstimate,
        openToNewEstimate,
    ]);

    const setPageAndPauseUndoFlag = useCallback(
        (page: PAGE): void => {
            if (page === PAGE.SUMMARY) {
                pageFromAnalytics({ title: `/${page}`, path: `/${page}` });
                track('summary_viewed');
                dispatch(setPauseUndoStackUpdatesForSummary(true));
            }
            setCurrentPage(page);
            if (page === PAGE.ESTIMATE) {
                dispatch(updateValidSmallTicketNumber(undefined)); //ensures that the ticket number must be re-entered if we go back to any editing
                setTimeout(() => {
                    dispatch(setPauseUndoStackUpdatesForSummary(false));
                }, RETURN_FROM_SUMMARY_STACK_PAUSE); // TODO turn this flag off when estimate page is definitely all loaded
            }
        },
        [dispatch]
    );

    const onCheckoutPressed = useCallback(() => {
        /* flushSync forces proper re-rendering BEFORE switching pages, which allows the canvas to update properly, since useEffect doesn't work on unmounted components. More about react batching:
        https://blog.bitsrc.io/automatic-batching-in-react-18-what-you-should-know-d50141dc096e */
        drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE);
        flushSync(() => {
            dispatch(resetSelectedCornerRadius());
            dispatch(resetSelectedDiagonalCorner());
            dispatch(resetSelectedCustomSide());
        });

        setPageAndPauseUndoFlag(PAGE.SUMMARY);
    }, [canvas, dispatch, setPageAndPauseUndoFlag]);

    const showLoader = isFetchingRetailerSettings || isFetchingEstimate;
    const hideEstimatePage = showLoader || currentPage !== PAGE.ESTIMATE;
    const hideSummaryPage = showLoader || currentPage !== PAGE.SUMMARY;

    return (
        <>
            {showLoader && <FullPageLoader />}
            <div hidden={hideEstimatePage}>
                <ScrollableArea onCheckoutPressed={onCheckoutPressed} />
            </div>
            <div hidden={hideSummaryPage}>
                <OrderSummaryPage
                    isHidden={hideSummaryPage}
                    onClose={() => {
                        dispatch(updateIsSavingEstimate(false));
                        setPageAndPauseUndoFlag(PAGE.ESTIMATE);
                    }}
                />
            </div>
        </>
    );
};

export default EasyEstimatorPage;
